'use client';

import { ArrowDown, ArrowUp, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { BottomSheet } from './bottom-sheet';

export type SortDir = 'asc' | 'desc';

/**
 * Ein Sortierfeld mit richtungsabhängigen Beschreibungen.
 * `descSub` wird bei absteigender, `ascSub` bei aufsteigender Richtung angezeigt
 * (z.B. „Höchster zuerst" / „Niedrigster zuerst").
 */
export interface SortFieldOption<K extends string = string> {
  id: K;
  label: string;
  ascSub: string;
  descSub: string;
}

export interface SortSheetProps<K extends string = string> {
  open: boolean;
  onClose: () => void;
  /** Überschrift des Sheets. Default: „Sortierung". */
  title?: string;
  options: SortFieldOption<K>[];
  sortKey: K;
  sortDir: SortDir;
  onChangeSortKey: (key: K) => void;
  onChangeSortDir: (dir: SortDir) => void;
}

/**
 * Geteiltes mobiles Sortier-Sheet für alle CRM-Routen (Artikelsuche, Belege,
 * Favoriten, Projekte). Vorbild: das Artikelsuche-Sheet.
 *
 * Verhalten wie das Vorbild: Feld- und Richtungswahl wirken **sofort** (Live-Vorschau
 * hinter dem Sheet); der „Übernehmen"-Button schließt nur. Generisch über die Feld-ID.
 */
export function SortSheet<K extends string = string>({
  open,
  onClose,
  title = 'Sortierung',
  options,
  sortKey,
  sortDir,
  onChangeSortKey,
  onChangeSortDir,
}: SortSheetProps<K>) {
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel={title}
      header={
        <div className="px-4 py-3 pr-12">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        </div>
      }
      footer={
        <button
          type="button"
          onClick={onClose}
          className="w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors"
        >
          <Check size={16} />
          Übernehmen
        </button>
      }
    >
      <div className="px-4 py-4 space-y-4">
        {/* Richtungs-Toggle */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onChangeSortDir('desc')}
            aria-pressed={sortDir === 'desc'}
            className={clsx(
              'flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors',
              sortDir === 'desc'
                ? 'border-primary bg-orange-50'
                : 'border-gray-200 bg-white hover:bg-gray-50',
            )}
          >
            <ArrowDown
              size={20}
              className={sortDir === 'desc' ? 'text-primary' : 'text-gray-500'}
            />
            <span className="text-sm font-medium text-gray-900">Absteigend</span>
            <span className="text-[11px] text-gray-500">9→0 · Z→A · neu→alt</span>
          </button>
          <button
            type="button"
            onClick={() => onChangeSortDir('asc')}
            aria-pressed={sortDir === 'asc'}
            className={clsx(
              'flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors',
              sortDir === 'asc'
                ? 'border-primary bg-orange-50'
                : 'border-gray-200 bg-white hover:bg-gray-50',
            )}
          >
            <ArrowUp
              size={20}
              className={sortDir === 'asc' ? 'text-primary' : 'text-gray-500'}
            />
            <span className="text-sm font-medium text-gray-900">Aufsteigend</span>
            <span className="text-[11px] text-gray-500">0→9 · A→Z · alt→neu</span>
          </button>
        </div>

        {/* Feldliste */}
        <section>
          <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Sortieren nach
          </h3>
          <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white">
            {options.map((option) => {
              const active = sortKey === option.id;
              const sublabel = sortDir === 'desc' ? option.descSub : option.ascSub;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onChangeSortKey(option.id)}
                  aria-pressed={active}
                  className={clsx(
                    'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors',
                    active ? 'bg-orange-50' : 'hover:bg-gray-50',
                  )}
                >
                  <div className="flex flex-col">
                    <span
                      className={clsx(
                        'text-sm',
                        active
                          ? 'text-primary font-semibold'
                          : 'text-gray-800 font-medium',
                      )}
                    >
                      {option.label}
                    </span>
                    <span className="text-[11px] text-gray-500">{sublabel}</span>
                  </div>
                  {active && <Check size={16} className="text-primary" />}
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </BottomSheet>
  );
}
